import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Request from '../../../../../../../helpers/request'
import * as url from '../../../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'
import { loadAddressForSwitch } from '../../../../../../../helpers/mesh_address'

interface Props {
    rooms: any[]
    relays: any[]
    pcbs: any[]
    switches: any[]
    devices: any[]
    placeId: string
    userId: string
    [key: string]: any
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const ActionBtn = ({ label, color, busy, onClick, title }: {
    label: string; color: string; busy: boolean; onClick: () => void; title?: string;
}) => (
    <button
        onClick={onClick}
        disabled={busy}
        title={title || label}
        style={{
            padding: '4px 10px', fontSize: 12, fontWeight: 500,
            background: '#fff', color, border: `1px solid ${color}`,
            borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
            marginRight: 4, opacity: busy ? 0.5 : 1,
        }}
    >
        {label}
    </button>
)

const renderOnline = (isOnline: boolean | null) => {
    if (isOnline === true) return <span style={{ color: '#16A34A' }}>● Online</span>
    if (isOnline === false) return <span style={{ color: '#9CA3AF' }}>○ Offline</span>
    return <span style={{ color: '#858D9D' }}>--</span>
}

const PlaceAppliances = ({ rooms, relays, switches, devices, pcbs, placeId, userId }: Props) => {
    const navigate = useNavigate()
    const [busyDeviceId, setBusyDeviceId] = useState<string | null>(null)
    const [controlsTarget, setControlsTarget] = useState<any>(null)
    const [page, setPage] = usePersistedPage(`place-appliances-${placeId}`)

    const placeRooms = (rooms || []).filter(r => r.placeId === placeId)
    const placeRelays = (relays || []).filter(r => r.placeId === placeId)
    const placeSwitches = (switches || []).filter(s => s.placeId === placeId)
    const placeDevices = (devices || []).filter(d => d.placeId === placeId)
    const pcbById: Record<string, any> = Object.fromEntries((pcbs || []).map((p: any) => [p.id, p]))

    const roomMap = Object.fromEntries(placeRooms.map(r => [r.id, r.name]))
    const relayMap = Object.fromEntries(placeRelays.map(r => [r.id, r.name]))

    // Map device.id → controlling switch (1st match). switch.deviceId is the link.
    const switchByDevice: Record<string, any> = {}
    for (const s of placeSwitches) {
        if (s.deviceId && !switchByDevice[s.deviceId]) switchByDevice[s.deviceId] = s
    }
    // Default (no-group) association: list all switches with deviceId === device.id.
    // When grouping lands (joint dev), expand via switch_devices junction.
    const controlsForDevice = (device: any) =>
        placeSwitches.filter((s: any) => s.deviceId === device.id)

    // Panel name is on the switch (device.relayId is not populated; device
    // links to relay via its controlling switch.relayId).
    const panelForDevice = (device: any) => {
        const sw = switchByDevice[device.id]
        const relayId = sw?.relayId || device.relayId
        return relayId ? (relayMap[relayId] || '') : ''
    }

    const sorted = [...placeDevices].sort((a, b) => {
        const ra = roomMap[a.roomId] || ''
        const rb = roomMap[b.roomId] || ''
        if (ra !== rb) return ra.localeCompare(rb)
        const pa = panelForDevice(a)
        const pb = panelForDevice(b)
        if (pa !== pb) return pa.localeCompare(pb)
        return (a.name || '').localeCompare(b.name || '')
    })

    const { paged: pagedDevices, totalPages, safePage } = paginate(sorted, page)

    const pingAppliance = (device: any) => {
        // Ping the host PCB (same path the Modules tab uses). The /ping-device
        // endpoint is a whole-place ping, not per-appliance — PING_PCB with the
        // controlling switch's pcbId targets just this module.
        const sw = switchByDevice[device.id]
        const pcbId = sw?.pcbId
        if (!pcbId) {
            toast.error(`No host module found for ${device.name}`)
            return
        }
        setBusyDeviceId(device.id)
        Request.post(url.PING_PCB, { moduleId: pcbId })
            .then(() => toast.success(`Ping sent to ${device.name}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Ping failed'))
            .finally(() => setBusyDeviceId(null))
    }

    return (
        <div>
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>Name</TH>
                            <TH>Panel</TH>
                            <TH>Room</TH>
                            <TH>Online</TH>
                            <TH>Unicast</TH>
                            <TH>Actions</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No appliances found for this place
                                </td>
                            </tr>
                        )}
                        {pagedDevices.map((device, idx) => {
                            const sw = switchByDevice[device.id]
                            const online = sw ? !!sw.switchIsOnline : null
                            return (
                                <tr key={device.id || idx}>
                                    <TD>{device.name || '--'}</TD>
                                    <TD>{panelForDevice(device) || '--'}</TD>
                                    <TD>
                                        {device.roomId ? (
                                            <button
                                                onClick={() => navigate(`/users/profile/${userId}/places/${placeId}/rooms/${device.roomId}`)}
                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1a73e8', fontSize: 14, textDecoration: 'underline' }}
                                            >
                                                {roomMap[device.roomId] || device.roomId}
                                            </button>
                                        ) : '--'}
                                    </TD>
                                    <TD>{renderOnline(online)}</TD>
                                    <TD>{(() => {
                                        const addr = loadAddressForSwitch(sw, sw ? pcbById[sw.pcbId] : null)
                                        return addr != null ? addr : '--'
                                    })()}</TD>
                                    <TD>
                                        <ActionBtn
                                            label="Ping"
                                            color="#1a73e8"
                                            busy={busyDeviceId === device.id}
                                            onClick={() => pingAppliance(device)}
                                            title="Ping this appliance's controlling switch"
                                        />
                                        <ActionBtn
                                            label="Manual Controls"
                                            color="#107569"
                                            busy={false}
                                            onClick={() => setControlsTarget(device)}
                                            title="Show manual controls linked to this appliance"
                                        />
                                        <ActionBtn
                                            label="Logs"
                                            color="#475467"
                                            busy={false}
                                            onClick={() => navigate(`/users/profile/${userId}/places/${placeId}/logs`)}
                                            title="Open the Logs tab for this place"
                                        />
                                    </TD>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <PaginationBar
                    page={safePage}
                    totalPages={totalPages}
                    total={sorted.length}
                    onPage={setPage}
                />
            </div>

            {controlsTarget && (() => {
                const linked = controlsForDevice(controlsTarget)
                return (
                    <div
                        data-testid="controls-dialog"
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                        }}
                        onClick={() => setControlsTarget(null)}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: 12, padding: 24,
                                maxWidth: 640, width: '90%', maxHeight: '80vh', overflow: 'auto',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            }}
                        >
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                                Manual Controls linked to <code style={{ fontSize: 13 }}>{controlsTarget.name}</code>
                            </h4>
                            {linked.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                    <thead>
                                        <tr>
                                            <TH>Name</TH>
                                            <TH>Room</TH>
                                            <TH>Online</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linked.map((s: any) => (
                                            <tr key={s.id}>
                                                <TD>{s.name || '--'}</TD>
                                                <TD>{roomMap[s.roomId] || '--'}</TD>
                                                <TD>{renderOnline(s.switchIsOnline ?? null)}</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No manual controls linked to this appliance
                                </div>
                            )}
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setControlsTarget(null)}
                                    style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
        </div>
    )
}

export default PlaceAppliances
